import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import D2CFeatureTabs from '../components/fulfillment/D2CFeatureTabs'
import AnalyticsMockup from '../components/fulfillment/AnalyticsMockup'
import SectorFlowAnimation from '../components/sector/SectorFlowAnimation'
import { orgSchema, breadcrumbSchema, faqPageSchema } from '../lib/seo'
import { LINKS } from '../lib/links'
import { useT } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'
import { FULFILLMENT_CONTENT } from '../data/fulfillment'
import type { Locale } from '../i18n'

/** audienceType du Service schema, par langue. */
const AUDIENCE: Record<Locale, string> = {
  fr: 'Marques DNVB et D2C',
  en: 'DNVB and D2C brands',
  nl: 'DNVB- en D2C-merken',
}

export default function FulfillmentPage() {
  const { t, locale } = useT()
  const content = FULFILLMENT_CONTENT[locale] ?? FULFILLMENT_CONTENT.fr
  const path = localizedPath('fulfillment', locale)
  const url = `https://yaslan.be${path}`

  return (
    <>
      <SeoHead
        title={content.seoTitle}
        description={content.metaDescription}
        path={path}
        jsonLd={[
          {
            '@type': 'Service',
            '@id': `${url}#service`,
            name: `${content.breadcrumb} — Yaslan`,
            serviceType: 'Fulfillment e-commerce D2C/DNVB',
            description: content.metaDescription,
            provider: { '@id': 'https://yaslan.be/#organization' },
            areaServed: ['Europe', 'United Kingdom', 'Switzerland'],
            audience: {
              '@type': 'BusinessAudience',
              audienceType: AUDIENCE[locale],
            },
            hasOfferCatalog: {
              '@type': 'OfferCatalog',
              name: content.edgeKicker,
              itemListElement: content.tabs.map((tab) => ({
                '@type': 'Offer',
                itemOffered: { '@type': 'Service', name: tab.title },
              })),
            },
            url,
          },
          faqPageSchema(content.faqs, { id: `${url}#faq`, inLanguage: locale }),
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: content.breadcrumb, path },
          ]),
        ]}
      />
      <main className="relative pt-28 sm:pt-32 pb-24 overflow-hidden isolate">
        {/* Background DA */}
        <div aria-hidden className="absolute inset-x-0 top-0 h-[1100px] -z-10 pointer-events-none">
          <div
            className="absolute inset-x-0 -top-20 h-[600px]"
            style={{
              background:
                'radial-gradient(ellipse at top, rgba(59,93,255,0.22), rgba(59,93,255,0.06) 45%, transparent 75%)',
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.28]"
            style={{
              backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
              maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 75%)',
            }}
          />
          <motion.div
            aria-hidden
            animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -right-32 top-[20%] w-[420px] h-[420px] rounded-full blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)',
            }}
          />
        </div>

        <div className="relative max-w-6xl mx-auto px-5 sm:px-6">
          {/* ───────────── HERO ───────────── */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs sm:text-sm text-ink-500"
          >
            <Link to={localizedPath('home', locale)} className="hover:text-accent-700 transition-colors">
              {t('breadcrumb.home')}
            </Link>
            <span className="mx-2 text-ink-300">/</span>
            <span className="text-ink-700">{content.breadcrumb}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{content.badge}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="mt-6 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(2.2rem,5vw,3.6rem)] leading-[1.06] max-w-3xl"
          >
            {content.titleLead}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.14em] pr-[0.08em] inline-block">
              {content.titleHighlight}
            </span>
            {content.titleTail}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 text-lg text-ink-600 leading-relaxed max-w-2xl"
          >
            {content.intro}
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.32 }}
            className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-700"
          >
            {content.proofPoints.map((p) => (
              <li key={p} className="flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="text-accent-600 shrink-0">
                  <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {p}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link
              to={`${localizedPath('contact', locale)}#devis`}
              className="relative group inline-flex items-center gap-2 bg-accent-700 hover:bg-accent-800 text-white rounded-full pl-5 pr-4 py-3 text-sm font-medium shadow-[0_16px_50px_-12px_rgba(59,93,255,0.65)] transition-colors overflow-hidden"
            >
              <span
                aria-hidden
                className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
              />
              <span className="relative">{content.ctaPrimary}</span>
              <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </Link>
            <Link
              to={localizedPath('pricing', locale)}
              className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-white/70 backdrop-blur px-5 py-3 text-sm font-medium text-accent-800 hover:bg-ink-100 hover:border-accent-300 transition-colors"
            >
              {content.ctaSecondary}
            </Link>
          </motion.div>

          {/* ───────────── DÉFINITION (passage citable) ───────────── */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative mt-16 sm:mt-20 rounded-2xl sm:rounded-3xl border border-ink-100 bg-ink-100 p-6 sm:p-8 lg:p-10"
          >
            <h2 className="text-xl sm:text-2xl font-semibold text-ink-950 tracking-tight">
              {content.defTitle}
            </h2>
            <p className="mt-4 text-sm sm:text-base text-ink-600 leading-relaxed max-w-4xl">
              {content.defText}
            </p>
          </motion.section>

          {/* ───────────── POURQUOI PAS GÉNÉRIQUE ───────────── */}
          <section className="relative mt-20 sm:mt-28">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-accent-700"
            >
              {content.whyKicker}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
              className="mt-4 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
            >
              {content.whyTitleLead}
              <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.14em] pr-[0.08em] inline-block">
                {content.whyTitleHighlight}
              </span>
              .
            </motion.h2>
            <div className="mt-6 space-y-5 text-ink-600 leading-relaxed max-w-3xl">
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {content.whyP1}
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.08 }}
              >
                {content.whyP2}
              </motion.p>
            </div>
          </section>

          {/* ───────────── L'AVANTAGE D2C (tabs) ───────────── */}
          <section className="relative mt-20 sm:mt-28">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
              </span>
              <span className="font-medium">{content.edgeKicker}</span>
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
              className="mt-5 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
            >
              {content.edgeTitleLead}
              <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.14em] pr-[0.08em] inline-block">
                {content.edgeTitleHighlight}
              </span>
              .
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 text-ink-600 max-w-2xl leading-relaxed"
            >
              {content.edgeIntro}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
              className="mt-10"
            >
              <D2CFeatureTabs tabs={content.tabs} tabCta={content.tabCta} locale={locale} />
            </motion.div>
          </section>

          {/* ───────────── DASHBOARD ───────────── */}
          <section className="relative mt-20 sm:mt-28">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-accent-700"
            >
              {content.dashKicker}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
              className="mt-4 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
            >
              {content.dashTitleLead}
              <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.14em] pr-[0.08em] inline-block">
                {content.dashTitleHighlight}
              </span>
              .
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 text-ink-600 max-w-2xl leading-relaxed"
            >
              {content.dashIntro}
            </motion.p>

            <div className="mt-10 grid lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-8 lg:gap-10 items-center">
              <div className="order-2 lg:order-1 space-y-4">
                {content.dashPoints.map((point, i) => (
                  <motion.div
                    key={point.title}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-start gap-3.5 rounded-2xl border border-ink-100 bg-ink-100 p-4 sm:p-5 hover:border-accent-200 transition-colors"
                  >
                    <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-accent-600 to-accent-700 text-white shrink-0 shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)] text-sm font-semibold">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold text-ink-950 tracking-tight">
                        {point.title}
                      </h3>
                      <p className="mt-0.5 text-sm text-ink-600 leading-relaxed">{point.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="order-1 lg:order-2">
                <AnalyticsMockup mock={content.mock} />
              </div>
            </div>
          </section>

          {/* ───────────── PROCESS ───────────── */}
          <section className="relative mt-20 sm:mt-28">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
            >
              {content.processTitle}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 text-ink-600 max-w-2xl leading-relaxed"
            >
              {content.processIntro}
            </motion.p>
            <div className="mt-10">
              <SectorFlowAnimation
                steps={content.flowSteps}
                badge={content.flowBadge}
                kicker={content.flowKicker}
              />
            </div>
          </section>

          {/* ───────────── CHIFFRES ───────────── */}
          <section className="relative mt-20 sm:mt-28">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase text-accent-700"
            >
              {content.statsKicker}
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
              className="mt-4 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
            >
              {content.statsTitleLead}
              <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.14em] pr-[0.08em] inline-block">
                {content.statsTitleHighlight}
              </span>
              .
            </motion.h2>

            <div className="mt-10 sm:mt-12 grid sm:grid-cols-3 gap-4 sm:gap-5">
              {content.stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="group relative rounded-2xl sm:rounded-3xl border border-ink-100 bg-ink-100 p-6 sm:p-8 hover:border-accent-200 hover:shadow-[0_24px_60px_-22px_rgba(59,93,255,0.4)] transition-all overflow-hidden"
                >
                  <motion.span
                    aria-hidden
                    className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl pointer-events-none opacity-0 group-hover:opacity-60 transition-opacity"
                    style={{
                      background: 'radial-gradient(circle, rgba(59,93,255,0.35), transparent 70%)',
                    }}
                  />
                  <div className="relative">
                    <div className="font-semibold tracking-[-0.04em] leading-none text-[clamp(2.2rem,5vw,3.4rem)] bg-gradient-to-br from-ink-950 via-accent-700 to-accent-500 bg-clip-text text-transparent">
                      {s.value}
                    </div>
                    <p className="mt-3 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-ink-500">
                      {s.label}
                    </p>
                    <p className="mt-2 text-sm sm:text-base text-ink-600 leading-relaxed">
                      {s.caption}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* ───────────── BANDEAU TARIFS ───────────── */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative mt-20 sm:mt-28"
          >
            <Link
              to={localizedPath('pricing', locale)}
              className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl sm:rounded-3xl border border-accent-200 bg-accent-50/70 p-6 sm:p-8 hover:bg-accent-50 hover:border-accent-300 transition-colors"
            >
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-ink-950 tracking-tight">
                  {content.pricingTitle}
                </h2>
                <p className="mt-1.5 text-sm sm:text-base text-ink-600 leading-relaxed max-w-xl">
                  {content.pricingText}
                </p>
              </div>
              <span className="inline-flex items-center gap-2 self-start sm:self-center shrink-0 bg-accent-700 group-hover:bg-accent-800 text-white rounded-full pl-5 pr-4 py-3 text-sm font-medium shadow-[0_16px_50px_-12px_rgba(59,93,255,0.65)] transition-colors">
                {content.pricingCta}
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </span>
            </Link>
          </motion.section>

          {/* ───────────── FAQ ───────────── */}
          <section className="relative mt-20 sm:mt-28">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
            >
              {content.faqTitle}
            </motion.h2>

            <div className="mt-10 space-y-3">
              {content.faqs.map((f, i) => (
                <motion.details
                  key={f.q}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="group rounded-2xl border border-ink-100 bg-ink-100 p-5 sm:p-6 hover:border-accent-200 transition-colors"
                >
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                    <h3 className="text-base sm:text-lg font-semibold text-ink-950 tracking-tight">
                      {f.q}
                    </h3>
                    <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full border border-accent-200 bg-accent-50 text-accent-700 flex items-center justify-center transition-transform group-open:rotate-45">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                      </svg>
                    </span>
                  </summary>
                  <p className="mt-3 text-sm sm:text-base text-ink-600 leading-relaxed">{f.a}</p>
                </motion.details>
              ))}
            </div>
          </section>

          {/* ───────────── CTA FINAL ───────────── */}
          <section className="relative mt-20 sm:mt-28">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-3xl bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 text-white px-6 sm:px-12 py-16 sm:py-24 overflow-hidden isolate"
            >
              <div
                aria-hidden
                className="absolute inset-0 opacity-30 -z-10"
                style={{
                  backgroundImage: 'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)',
                  backgroundSize: '28px 28px',
                  maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
                }}
              />
              <motion.div
                aria-hidden
                animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -right-32 -top-32 w-[460px] h-[460px] rounded-full blur-3xl -z-10"
                style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.55), transparent 70%)' }}
              />

              <div className="relative max-w-3xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-accent-200">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-70 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
                  </span>
                  {content.ctaBadge}
                </span>

                <h2 className="mt-6 font-semibold tracking-[-0.035em] text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.02]">
                  {content.ctaTitleLead}
                  <span className="font-semibold bg-gradient-to-r from-accent-300 via-accent-200 to-white bg-clip-text text-transparent pb-[0.12em] pr-[0.08em] inline-block">
                    {content.ctaTitleHighlight}
                  </span>
                  .
                </h2>

                <p className="mt-5 sm:mt-6 text-base sm:text-lg text-ink-300 leading-relaxed max-w-xl">
                  {content.ctaCopy}
                </p>

                <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
                  <Link
                    to={`${localizedPath('contact', locale)}#devis`}
                    className="relative group inline-flex items-center gap-2.5 bg-ink-100 hover:bg-accent-50 text-ink-950 rounded-full pl-6 pr-5 py-3.5 sm:py-4 text-sm sm:text-base font-semibold shadow-[0_24px_60px_-18px_rgba(59,93,255,0.7)] transition-colors overflow-hidden"
                  >
                    <span
                      aria-hidden
                      className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-accent-200/60 to-transparent pointer-events-none"
                    />
                    <span className="relative">{content.ctaFinalPrimary}</span>
                    <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-ink-950 text-white">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </Link>
                  <Link
                    to={`${localizedPath('contact', locale)}#rendez-vous`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-5 py-3.5 sm:py-4 text-sm sm:text-base font-medium text-white/90 hover:bg-white/10 hover:border-white/30 transition-colors"
                  >
                    {content.ctaFinalSecondary}
                  </Link>
                </div>

                <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs sm:text-sm text-ink-400">
                  <span className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    {content.trust1}
                  </span>
                  <span className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                      <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {content.trust2}
                  </span>
                  <span className="flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="text-accent-400">
                      <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {content.trust3}
                  </span>
                </div>
              </div>
            </motion.div>
          </section>
        </div>
      </main>
    </>
  )
}
